import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { authService } from '../services/auth';
import { useToast } from '../hooks/useToast';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { Stack, Container } from '../design-system/components/Layout';
import { Icon } from '../components/common/Icon';
import { iconMappings } from '../utils/iconMappings';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { hexToRgba } from '../utils/colorUtils';

/** Share in-flight confirm POSTs across StrictMode remounts. */
const confirmEmailInflight = new Map<string, Promise<void>>();

const ConfirmationContainer = styled(Container)`
  max-width: 600px;
  padding: ${spacing[10]} ${spacing[5]};
  background: ${props => props.theme.colors.gradientBackground};
`;

const ConfirmationCard = styled(Card)`
  padding: ${spacing[8]};
  text-align: center;
`;

const IconWrapper = styled.div<{ success: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => hexToRgba(
    props.success ? props.theme.colors.success : props.theme.colors.error, 
    0.1
  )};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing[6]};
`;

const EmailConfirmation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { success: showSuccess, error: showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const confirmEmail = async () => {
      const userId = searchParams.get('userId');
      const token = searchParams.get('token');

      if (!userId || !token) {
        if (!cancelled) {
          setError(t('register.invalidConfirmationLink'));
          setLoading(false);
        }
        return;
      }

      const attemptKey = `${userId}:${token}`;
      let request = confirmEmailInflight.get(attemptKey);
      if (!request) {
        request = authService.confirmEmail(userId, token);
        confirmEmailInflight.set(attemptKey, request);
      }

      try {
        await request;
        if (cancelled) {
          return;
        }
        setConfirmed(true);
        showSuccess(t('register.emailConfirmed'));
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || t('register.confirmationFailed'));
          showError(err.message || t('register.confirmationFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    confirmEmail();
    return () => {
      cancelled = true;
    };
    // Run for the current link; avoid re-POSTing on t/toast identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (loading) {
    return (
      <ConfirmationContainer>
        <ConfirmationCard variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
          <Stack direction="column" spacing="lg" align="center">
            <LoadingSpinner />
            <Text size="base" color="secondary">
              {t('register.confirmingEmail')}
            </Text>
          </Stack>
        </ConfirmationCard>
      </ConfirmationContainer>
    );
  }

  return (
    <ConfirmationContainer>
      <ConfirmationCard variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
        <Stack direction="column" spacing="lg" align="center">
          <IconWrapper success={confirmed}>
            <Icon 
              icon={confirmed ? iconMappings.success : iconMappings.error} 
              size="xl" 
              color={confirmed ? 'success' : 'error'} 
            />
          </IconWrapper>

          {confirmed ? (
            <>
              <div>
                <Heading variant="h2" color="text" style={{ marginBottom: spacing[4] }}>
                  {t('register.emailConfirmed')}
                </Heading>
                <Text size="base" color="secondary">
                  {t('register.emailConfirmedMessage')}
                </Text>
              </div>
              <Button
                variant="gradient"
                glow
                ripple
                onClick={() => navigate('/login')}
              >
                {t('login.signIn')}
              </Button>
            </>
          ) : (
            <>
              <div>
                <Heading variant="h2" color="text" style={{ marginBottom: spacing[4] }}>
                  {t('register.confirmationFailed')}
                </Heading>
                <Text size="base" color="secondary" style={{ marginBottom: spacing[4] }}>
                  {error || t('register.confirmationFailedMessage')}
                </Text>
              </div>
              <Stack direction="column" spacing="md" style={{ width: '100%' }}>
                <Button
                  variant="gradient"
                  glow
                  ripple
                  onClick={() => navigate('/register')}
                >
                  {t('register.registerAgain')}
                </Button>
                <Text size="sm" color="secondary">
                  {t('register.alreadyHaveAccount')}
                  <Link to="/login" style={{ color: theme.colors.primary, fontWeight: typography.fontWeight.semibold }}>
                    {t('login.signIn')}
                  </Link>
                </Text>
              </Stack>
            </>
          )}
        </Stack>
      </ConfirmationCard>
    </ConfirmationContainer>
  );
};

export default EmailConfirmation;
